import {
	useDuplicateSector,
	usePostSector,
	useUpdateAvatarSector,
	useUpdateSector,
} from '@practium/features/api';
import { useToast } from '@practium/ui';
import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { DEFAULT_SECTOR_FORM, SECTOR_FIELD_LABELS } from '../constants/sector.constants';
import { SectorSchema } from '../schemas/sector.schema';
import type { SectorFormData } from '../types';
import { SectorIrrigationData } from '../types/properties.types';

export interface UseSectorFormOptions {
	initialData?: SectorFormData;
	propId?: string;
	onSubmit: (createdId?: string) => void;
	avatarFile?: File | null;
}

const computeAmountOfPlants = (size: number, lineSpacing: number, plantSpacing: number): number => {
	const s = size;
	const ls = lineSpacing;
	const ps = plantSpacing;
	if (!s || !ls || !ps) return 0;
	return Math.round((s * 10000) / (ls * ps));
};

const normalizeInitialForm = (initialData?: SectorFormData): SectorFormData => ({
	...DEFAULT_SECTOR_FORM,
	...initialData,
	irrigation: {
		...DEFAULT_SECTOR_FORM.irrigation,
		...initialData?.irrigation,
	},
});

const toOptionalNumber = (value: number | string | undefined) => {
	if (value === undefined || value === '') return undefined;
	return Number(value);
};

export const useSectorForm = ({
	initialData,
	propId = '',
	onSubmit,
	avatarFile,
}: UseSectorFormOptions) => {
	const toast = useToast();
	const [searchParams, setSearchParams] = useSearchParams();

	const sectorId = searchParams.get('id');
	const isEditing = Boolean(sectorId);

	const initialForm = normalizeInitialForm(initialData);
	const [form, setForm] = useState<SectorFormData>(initialForm);
	const initialFormRef = useRef({
		...initialForm,
		amountOfPlants: computeAmountOfPlants(
			initialForm.size,
			initialForm.lineSpacing,
			initialForm.plantSpacing
		),
	});

	const setIrrigation = (
		key: keyof SectorIrrigationData,
		value: string | number
	) => {
		setForm((f) => ({
			...f,
			irrigation: {
				...f.irrigation,
				[key]: value
			}
		}))
	}

	const [isLoading, setIsLoading] = useState(false);
	const [errors, setErrors] = useState<Record<string, string>>({});
	const [replicateCount, setReplicateCount] = useState(0);

	const { mutateAsync: createSector } = usePostSector();
	const { mutateAsync: updateSector } = useUpdateSector();
	const { mutateAsync: updateAvatarSector } = useUpdateAvatarSector();
	const { mutateAsync: duplicateSector } = useDuplicateSector();

	useEffect(() => {
		const computed = computeAmountOfPlants(form.size, form.lineSpacing, form.plantSpacing);
		setForm((f) => ({ ...f, amountOfPlants: computed }));
	}, [form.size, form.lineSpacing, form.plantSpacing]);

	const set = (key: keyof SectorFormData, value: string | number) =>
		setForm((f) => ({ ...f, [key]: value }));

	const isDirty = () =>
		JSON.stringify(form) !== JSON.stringify(initialFormRef.current) || replicateCount > 0;

	const handleSubmit = async () => {
		const result = SectorSchema.safeParse(form);

		if (!result.success) {
			const fieldErrors: Record<string, string> = {};
			result.error.issues.forEach((issue) => {
				const path = String(issue.path[0] ?? '');
				if (path) fieldErrors[path] = issue.message;
			});
			setErrors(fieldErrors);
			const uniquePaths = [
				...new Set(result.error.issues.map((i) => String(i.path[0] ?? '')).filter(Boolean)),
			];
			const labels = uniquePaths.map((p) => SECTOR_FIELD_LABELS[p] ?? p).join(', ');
			toast.error(`Campos inválidos: ${labels}`);
			return;
		}

		setErrors({});
		setIsLoading(true);
		try {
			const payload = {
				name: form.name,
				areaId: form.areaId,
				size: Number(form.size),
				plantingDate: form.plantingDate,
				amountOfPlants: form.amountOfPlants,
				plantSpacing: Number(form.plantSpacing),
				lineSpacing: Number(form.lineSpacing),
				irrigationEfficiency: toOptionalNumber(form.irrigation.irrigationEfficiency),
				grossIrrigationDepth: toOptionalNumber(form.irrigation.grossIrrigationDepth),
				operatingTime: toOptionalNumber(form.irrigation.operatingTime),
				dripperSpacing: toOptionalNumber(form.irrigation.dripperSpacing),
				dripperFlow: toOptionalNumber(form.irrigation.dripperFlow),
				sprinklerSpacing: toOptionalNumber(form.irrigation.sprinklerSpacing),
				sprinklerFlow: toOptionalNumber(form.irrigation.sprinklerFlow),
				microSprinklerSpacing: toOptionalNumber(form.irrigation.microSprinklerSpacing),
				microSprinklerFlow: toOptionalNumber(form.irrigation.microSprinklerFlow),
				avatar: avatarFile,
			};

			if (isEditing && sectorId) {
				await updateSector({ propId, areaId: form.areaId, sectorId, data: payload });
				toast.success('Setor salvo com sucesso!');

				if (avatarFile instanceof File) {
					try {
						await updateAvatarSector({
							propId,
							areaId: form.areaId,
							sectorId,
							avatar: avatarFile,
						});
					} catch {
						toast.error('Setor atualizado, mas não foi possível atualizar o avatar.');
					}
				}

				if (replicateCount > 0) {
					try {
						await duplicateSector({
							propId,
							areaId: form.areaId,
							sectorId,
							amount: replicateCount,
						});
						toast.success(`Setor replicado ${replicateCount}x com sucesso!`);
					} catch {
						toast.error('Setor atualizado, mas não foi possível replicá-lo.');
					}
				}

				onSubmit();
			} else {
				const created = await createSector({ propId, areaId: form.areaId, data: payload });
				toast.success('Setor criado com sucesso!');

				if (replicateCount > 0 && created?._id) {
					try {
						await duplicateSector({
							propId,
							areaId: form.areaId,
							sectorId: created._id,
							amount: replicateCount,
						});
						toast.success(`Setor replicado ${replicateCount}x com sucesso!`);
					} catch {
						toast.error('Setor criado, mas não foi possível replicá-lo.');
					}
				}

				if (created?._id) {
					const createdIdValue = created._id;
					const createdAreaId = form.areaId;
					setSearchParams((prev) => {
						const next = new URLSearchParams(prev);
						next.set('id', createdIdValue);
						next.set('areaId', createdAreaId);
						return next;
					});
				}

				onSubmit(created?._id);
			}
		} catch (err) {
			toast.error(err instanceof Error ? err.message : 'Erro ao salvar setor');
		} finally {
			setIsLoading(false);
		}
	};

	return {
		form,
		errors,
		isLoading,
		set,
		setIrrigation,
		handleSubmit,
		isEditing,
		replicateCount,
		setReplicateCount,
		isDirty,
	};
};
